import { useTranslate } from "ra-core";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatInt, formatPercent } from "../../domain/format";

export type ChartBar = {
  key: string;
  label: string;
  value: number;
  previous: number | null;
  conversion: number | null;
  detail: string;
  /** The bar's colour (deal stages); the brand series colour when absent. */
  color?: string;
};

// dataviz: single series = brand-600 (#E5630F, ≥ 3:1 on both surfaces, in-band light+dark); context = stone.
const SERIES = "bg-brand-600";
const CONTEXT = "bg-stone-500 dark:bg-stone-400";

/** Horizontal funnel: thin bars anchored at the baseline, 4 px rounded data end, values as text, conversion between rows. */
export const FunnelChart = ({
  title,
  bars,
  comparison = false,
}: {
  title: string;
  bars: readonly ChartBar[];
  comparison?: boolean;
}) => {
  const translate = useTranslate();
  const max = Math.max(
    1,
    ...bars.map((b) => Math.max(b.value, comparison ? (b.previous ?? 0) : 0)),
  );
  const width = (v: number) => `max(2px, ${(v / max) * 100}%)`;
  return (
    <figure aria-label={title} className="w-full">
      <figcaption className="mb-3 flex items-center justify-between gap-2">
        <span className="text-sm font-bold">{title}</span>
        {comparison && (
          <span className="flex gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <span
                className={`size-2.5 rounded-sm ${SERIES}`}
                aria-hidden="true"
              />
              {translate("gavion.reports.legend.current")}
            </span>
            <span className="flex items-center gap-1">
              <span
                className={`size-2.5 rounded-sm ${CONTEXT}`}
                aria-hidden="true"
              />
              {translate("gavion.reports.legend.previous")}
            </span>
          </span>
        )}
      </figcaption>
      <ol className="space-y-0.5">
        {bars.map((bar) => (
          <li key={bar.key}>
            {bar.conversion != null && (
              <p className="py-0.5 pl-[min(10rem,35%)] text-xs text-muted-foreground tabular-nums">
                <span className="sr-only">
                  {translate("gavion.reports.conversion_label")}{" "}
                </span>
                ↓ {formatPercent(bar.conversion)}
              </p>
            )}
            <div className="grid grid-cols-[min(10rem,35%)_1fr] items-center gap-3">
              <span className="truncate text-sm">{bar.label}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    tabIndex={0}
                    className="flex min-h-7 flex-col justify-center gap-[2px] rounded outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-5 rounded-r-[4px] ${bar.color ? "" : SERIES}`}
                        style={{
                          width: width(bar.value),
                          ...(bar.color ? { backgroundColor: bar.color } : {}),
                        }}
                      />
                      <span className="text-sm font-bold tabular-nums">
                        {formatInt(bar.value)}
                      </span>
                    </div>
                    {comparison && bar.previous != null && (
                      <div
                        className={`h-1.5 rounded-r-[4px] ${CONTEXT}`}
                        style={{ width: width(bar.previous) }}
                      />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-bold">
                    {bar.label}: {formatInt(bar.value)}
                  </p>
                  {bar.detail && <p>{bar.detail}</p>}
                  {comparison && bar.previous != null && (
                    <p>
                      {translate("gavion.reports.legend.previous")}:{" "}
                      {formatInt(bar.previous)}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </div>
          </li>
        ))}
      </ol>
    </figure>
  );
};
